import type { SchemaObject } from 'ajv';
import _ from 'lodash';
import { propertyTypePatchEntry } from './propertyTypeValidation';
import type { PatchCollection, PatchEntry, SchemaPatches } from './types';

// This needs to be in sync with backend
export const passwordMinLength = 12;

export const errorMessagePatchEntry: PatchEntry = {
  // ajv-errors requires the keyword to be named `errorMessage`, but bare `errorMessage`
  // is not legal OpenAPI 3.0, so the backend emits `x-error-messages` instead
  condition: (schema) => _.has(schema, 'x-error-messages'),
  patch: (schema) => {
    schema.errorMessage = schema['x-error-messages'];
    delete schema['x-error-messages'];
  },
};

export const globalPatches: PatchCollection = [
  propertyTypePatchEntry,
  {
    // remove discriminator mapping for validation
    // AJV does not support mapping and complains but mapping is needed for api generator
    condition: (schema) => schema.discriminator?.mapping,
    patch: (schema) => {
      delete schema.discriminator.mapping;
    },
  },
  {
    // Nullable References and Enums
    condition: () => true,
    patch: (schema) => {
      _.forEach(schema.properties, (value) => {
        if (_.has(value, 'oneOf') && value.nullable) {
          value.oneOf = [
            ...value.oneOf,
            {
              type: 'null',
              nullable: true,
            },
          ];

          delete value.type;
          delete value.nullable;
        }
      });
    },
  },
  errorMessagePatchEntry,
];

export const schemaPatches: SchemaPatches = {
  TenantEmailSettingsDto: (schema) => {
    schema.properties.defaultFromAddress.format = 'email';
  },
  TenantEmailSettingsEditDto: (schema) => {
    schema.properties.defaultFromAddress.format = 'email';
  },
  EmailSettingsEditDto: (schema) => {
    schema.properties.defaultFromAddress.format = 'email';
  },
  CreateInitialAdminUserDto: (schema) => {
    schema.properties.email.format = 'email';
    schema.properties.password.minLength = passwordMinLength;
    schema.required.push('emailRepeated');
    schema.properties = {
      ...schema.properties,
      emailRepeated: {
        const: { $data: '1/email' },
        format: 'email',
        type: 'string',
      },
    };
  },
  ChangePasswordInput: (schema) => {
    // minLength is not applied for password
    schema.properties.newPassword.minLength = passwordMinLength;
  },
  ConfirmEmailRequestDto: (schema) => {
    schema.properties.newPassword.minLength = passwordMinLength;
  },
  ResetPasswordRequestDto: (schema) => {
    schema.properties.newPassword.minLength = passwordMinLength;
  },
  NumericListDto: (schema) => {
    schema.properties.values.items.minimum = { $data: '2/min' };
    schema.properties.values.items.maximum = { $data: '2/max' };
    schema.if = {
      properties: { valueType: { const: 'IntegerNumber' } },
    };
    schema.then = {
      properties: {
        values: {
          items: {
            type: 'integer',
          },
        },
      },
    };
  },
  EnumerationValueListDto: (schema) => {
    schema.if = { properties: { isEnabled: { const: true } } };
    schema.then = {
      properties: {
        selectedOptions: {
          ...schema.properties.selectedOptions,
          minItems: 1,
          nullable: false,
        },
      },
    };
  },

  TextValueDto: (schema) => {
    schema.if = { properties: { isEnabled: { const: true } } };
    schema.then = { required: [...schema.required, 'value'] };
  },

  DiscreteNumericListDto: (schema) => {
    schema.if = { properties: { isEnabled: { const: true } } };
    schema.then = {
      properties: {
        selectedValues: {
          ...schema.properties.selectedValues,
          minItems: 1,
          nullable: false,
        },
      },
    };
  },

  DiscreteNumericValueDto: (schema) => {
    schema.if = { properties: { isEnabled: { const: true } } };
    schema.then = {
      properties: {
        value: {
          ...schema.properties.value,
          minItems: 1,
          nullable: false,
        },
      },
    };
  },

  EnumerationValueDto: (schema) => {
    const patchedOneOf = [...schema.properties.value.oneOf];
    patchedOneOf.splice(-1);

    schema.if = {
      properties: { isEnabled: { const: true } },
    };
    schema.then = {
      required: [...schema.required, 'value'],

      properties: {
        value: {
          ...patchedOneOf[0],
        },
      },
    };
  },

  TaskItemAreaDto: (schema) => {
    schema.required.push('medicalSystemSetup');
  },
  GeometryLabelDto: (schema) => {
    schema.required.push('name');
  },
  EnableMaintenanceModeRequestDto: (schema) => {
    schema.properties.password.minLength = passwordMinLength;
  },
  SetupQuantityUsageDto: (schema) => {
    schema.properties.inputRangeMax.minimum = { $data: '1/inputRangeMin' };
    schema.properties.inputRangeMin.maximum = { $data: '1/inputRangeMax' };
    schema.if = {
      properties: { valueType: { const: 'IntegerNumber' } },
    };
    schema.then = {
      properties: {
        inputRangeMin: {
          type: 'integer',
        },
        inputRangeMax: {
          type: 'integer',
        },
      },
    };
  },
  // Note: CreateSetupQuantityUsageDto patches are not needed because they are not validated in forms, they are only used for API requests.
  DefinitionParameterDto: (schema) => {
    schema.if = {
      properties: { valueType: { const: 'IntegerNumber' } },
    };
    schema.then = {
      properties: {
        inputRangeMin: {
          type: 'integer',
        },
        inputRangeMax: {
          type: 'integer',
        },
      },
    };
  },
  // Note: CreateDefinitionParameterDto patches are not needed because they are not validated in forms, they are only used for API requests.

  // Validate DefinitionParameterValueDto values based on their own valueType field
  // Since the API now includes valueType on each value, we can validate directly at the value level
  DefinitionParameterValueDto: (schema) => {
    schema.if = {
      properties: { valueType: { const: 'IntegerNumber' } },
    };
    schema.then = {
      properties: {
        value: { type: 'integer' },
      },
    };
  },

  NumericEvaluationParameterDto: (schema) => {
    schema.if = {
      allOf: [
        {
          properties: {
            definition: {
              type: 'object',
              properties: { valueType: { const: 'IntegerNumber' } },
            },
          },
        },
        {
          properties: {
            evaluationType: {
              not: {
                enum: ['Ratio', 'RelativeDifference'],
              },
            },
          },
        },
      ],
    };
    schema.then = {
      properties: {
        min: { type: 'integer' },
        max: { type: 'integer' },
        lowerFailedLimit: { type: 'integer' },
        lowerWarningLimit: { type: 'integer' },
        upperWarningLimit: { type: 'integer' },
        upperFailedLimit: { type: 'integer' },
      },
    };
  },
};

export function applyPatch(
  patches: SchemaPatches,
  spec: SchemaObject,
  dtoName: string,
  schema: SchemaObject,
): SchemaObject {
  _.get(patches, dtoName, () => undefined)(schema, spec);
  return schema;
}

export function applyGlobalPatches(spec: SchemaObject, patches: PatchCollection = globalPatches) {
  const combinedPatch = (
    schema: SchemaObject,
    key: string | undefined,
    parent: SchemaObject | undefined,
  ) => {
    patches.forEach(({ condition, patch }) => {
      if (condition(schema, spec, key, parent)) {
        patch(schema, key, parent);
      }
    });
  };

  function apply(
    patch: (value: object, key?: string, parent?: object) => void,
    value: object,
    key?: string,
    parent: object | undefined = undefined,
  ) {
    if (_.isObject(value)) {
      patch(value, key, parent);
      _.forOwn(value, (childValue, childKey) => apply(patch, childValue, childKey, value));
    }
  }

  apply(combinedPatch, spec);
}
